import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useI18n } from '../../contexts/I18nContext';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { tuningAPI } from '../../services/api';
import { Car, FileUp, Search, AlertTriangle, Loader2, Zap, Check, RefreshCw } from 'lucide-react';

const BENSKY_URL = 'https://portal.bensky.co.uk/w.php?id=652&color=ff0000&differencecolor=ff0000';

const CATEGORY_LABELS = {
  performance: 'Leistung',
  system: 'System',
  emission: 'Abgas & Umwelt',
  comfort: 'Komfort',
  sound: 'Sound',
  transmission: 'Getriebe',
  custom: 'Sonstiges',
};

const VehiclesPage = () => {
  const { t } = useI18n();
  const iframeRef = useRef(null);
  const [tuningOptions, setTuningOptions] = useState([]);
  const [creditPriceEur, setCreditPriceEur] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [loading, setLoading] = useState(true);

  const fetchOptions = useCallback(async () => {
    try {
      const res = await tuningAPI.getOptions();
      const opts = res.data?.options || {};
      setCreditPriceEur(res.data?.credit_price_eur || 0);
      setTuningOptions(Object.entries(opts).map(([id, o]) => ({ id, ...o })));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOptions(); }, [fetchOptions]);

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data?.dataType === 'vehicle-data') {
        console.log('Bensky vehicle:', event.data.vehicleName);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  const resetConfigurator = () => {
    setIframeLoaded(false);
    setIframeKey(prev => prev + 1);
  };

  const toggleOption = (id) => {
    setSelectedOptions(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const calculateCredits = () => {
    return tuningOptions
      .filter(o => selectedOptions.includes(o.id))
      .reduce((sum, o) => sum + (o.credits || 0), 0);
  };

  const grouped = tuningOptions.reduce((acc, opt) => {
    const cat = opt.category || 'custom';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(opt);
    return acc;
  }, {});

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="vehicles-page">
        <div>
          <h1 className="font-heading text-2xl font-bold text-white">{t('nav.vehicles')}</h1>
          <p className="text-slate-400 mt-1">Chiptuning Konfigurator — Leistungsdaten & Tuning-Optionen</p>
        </div>

        {/* Bensky Configurator - clipped to hide unwanted elements */}
        <div className="glass-card p-6 relative">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-white flex items-center gap-2">
              <Car className="w-5 h-5 text-[#EC2229]" />
              Fahrzeug Konfigurator
            </h2>
            <button
              onClick={resetConfigurator}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#EC2229] text-white rounded-lg text-sm hover:bg-[#EC2229]/90 transition-colors"
              data-testid="reset-configurator"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              Neu starten
            </button>
          </div>

          {!iframeLoaded && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 text-[#EC2229] animate-spin mr-3" />
              <span className="text-slate-400 text-sm">Konfigurator wird geladen...</span>
            </div>
          )}

          {/* Bensky Configurator - full width, responsive height */}
          <div
            className="bensky-clip-wrapper relative"
            data-testid="bensky-configurator"
          >
            <iframe
              key={iframeKey}
              ref={iframeRef}
              src={BENSKY_URL}
              style={{
                border: '0px',
                width: '100%',
                minHeight: '700px',
                display: iframeLoaded ? 'block' : 'none',
              }}
              allowTransparency="true"
              referrerPolicy="origin"
              title="Fahrzeug Konfigurator"
              onLoad={() => setIframeLoaded(true)}
            />
          </div>
          <p className="text-[10px] text-slate-600 mt-2">Powered by Bensky — Leistungsdaten sind Richtwerte</p>
        </div>

        {/* Tuning Options */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-3">
            <div className="glass-card p-6">
              <h2 className="font-heading text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-[#EC2229]" />
                Verfügbare Tuning-Optionen
                <span className="text-xs text-slate-500 font-normal ml-2">Optionen wählen um Credits zu berechnen</span>
              </h2>

              {loading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-[#EC2229]" /></div>
              ) : (
                <div className="space-y-6">
                  {Object.entries(grouped).map(([category, options]) => (
                    <div key={category}>
                      <h3 className="text-sm font-medium text-slate-400 mb-2 uppercase tracking-wider">
                        {CATEGORY_LABELS[category] || category}
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                        {options.map(option => {
                          const selected = selectedOptions.includes(option.id);
                          return (
                            <button
                              key={option.id}
                              onClick={() => toggleOption(option.id)}
                              className={`text-left p-3 rounded-lg border transition-all ${
                                selected
                                  ? 'bg-[#EC2229]/10 border-[#EC2229]/40'
                                  : 'bg-white/[0.03] border-white/[0.06] hover:border-white/[0.15]'
                              }`}
                              data-testid={`tuning-option-${option.id}`}
                            >
                              <div className="flex items-start justify-between mb-1">
                                <div className="flex items-center gap-2">
                                  {selected && <Check className="w-3.5 h-3.5 text-[#EC2229] flex-shrink-0" />}
                                  <p className="text-white font-semibold text-sm">{option.name}</p>
                                </div>
                                <div className="text-right flex-shrink-0 ml-2">
                                  <span className="text-[#EC2229] font-mono font-bold text-sm">{option.credits} Cr</span>
                                  {creditPriceEur > 0 && <p className="text-[10px] text-slate-500 font-mono">{(option.credits * creditPriceEur).toFixed(2)} EUR</p>}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <div className="glass-card p-6 sticky top-6">
              <h3 className="font-heading text-lg font-semibold text-white mb-4">Credit-Rechner</h3>
              <div className="space-y-3 mb-4">
                {selectedOptions.length === 0 ? (
                  <p className="text-sm text-slate-500">Wählen Sie Tuning-Optionen aus um die Credits zu berechnen</p>
                ) : (
                  tuningOptions.filter(o => selectedOptions.includes(o.id)).map(o => (
                    <div key={o.id} className="flex justify-between items-center text-sm">
                      <span className="text-slate-300 truncate mr-2">{o.name}</span>
                      <span className="text-white font-mono flex-shrink-0">{o.credits} Cr</span>
                    </div>
                  ))
                )}
              </div>
              <div className="pt-3 border-t border-white/[0.06]">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-slate-400">Gesamt Credits</span>
                  <span className="text-2xl font-heading font-bold text-[#EC2229]">{calculateCredits().toFixed(1)}</span>
                </div>
                {creditPriceEur > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-slate-500">Geschätzter Preis</span>
                    <span className="text-sm font-mono text-slate-300">{(calculateCredits() * creditPriceEur).toFixed(2)} EUR</span>
                  </div>
                )}
              </div>
              {selectedOptions.length > 0 && (
                <Link to="/upload" className="mt-4 w-full flex items-center justify-center gap-2 py-2.5 bg-[#EC2229] text-white rounded-lg hover:bg-[#EC2229]/90 transition-colors text-sm font-medium" data-testid="start-upload-btn">
                  <FileUp className="w-4 h-4" /> Datei hochladen
                </Link>
              )}
            </div>

            <div className="glass-card p-6">
              <h3 className="font-heading text-sm font-semibold text-white mb-3">Schnellzugriff</h3>
              <div className="space-y-2">
                <Link to="/upload" className="flex items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-sm text-white" data-testid="quick-upload">
                  <FileUp className="w-4 h-4 text-[#EC2229]" /> Neue Datei hochladen
                </Link>
                <Link to="/dtc" className="flex items-center gap-2 p-2 bg-white/5 rounded-lg hover:bg-white/10 transition-colors text-sm text-white" data-testid="quick-dtc">
                  <Search className="w-4 h-4 text-[#EC2229]" /> DTC Code suchen
                </Link>
              </div>
            </div>

            <div className="glass-card p-4 border-l-4 border-amber-500">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-400">Die angezeigten Leistungswerte sind Richtwerte. Tatsächliche Ergebnisse können variieren.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default VehiclesPage;
